/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { DataTable } from 'app/components/ui/data-table';
import { Combobox } from 'app/components/ui/combobox';
import { Input } from 'app/components/ui/input';
import { ColumnDef } from '@tanstack/react-table';
import { AreaTrendChart, chartColors, chartPalette } from 'app/components/organisms/charts/charts';
import { appRoutes } from 'app/routes';
import {
  useCryptoGestOverview,
  useCryptoGestSettings,
  useCryptoGestValueHistory,
  useSaveCryptoGestChartInterval,
  useSyncAllCryptoGestWallets,
  CgAssetRow
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { amount, paddedDomain, pct, signClass } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { useQueryState } from 'app/hooks/use-query-state';
import { StatCard } from '../stat-card';
import { AssetIcon } from '../asset-icon';
import { cn } from 'app/lib/utils';
import { AlertTriangle, RefreshCw, Search } from 'lucide-react';
import moment from 'moment';

/**
 * Value-over-time buckets. The selected interval is the bucket size (one point
 * per interval); the full series runs back to the first entry and scrolls.
 */

const VALUE_INTERVALS: Array<{ key: string; label: string; intraday: boolean }> = [
  { key: '15m', label: '15m', intraday: true },
  { key: '30m', label: '30m', intraday: true },
  { key: '1h', label: '1h', intraday: true },
  { key: '4h', label: '4h', intraday: true },
  { key: '12h', label: '12h', intraday: true },
  { key: '24h', label: '24h', intraday: false },
  { key: '3d', label: '3d', intraday: false },
  { key: '1w', label: '1 sem', intraday: false }
];

/**
 * How many assets to show individually before grouping the rest into "Other".
 */

const ALLOCATION_TOP = 5;

/**
 * Neutral colour for the grouped "Other" slice.
 */

const OTHER_COLOR = '#9aa0a6';

/**
 * A single slice of a segmented distribution bar.
 */

type Slice = { key: string; label: string; pct: number; color: string };

/**
 * Build coloured slices from weighted items, grouping everything beyond the top
 * few into a neutral "Other" slice.
 */

function buildSlices(items: Array<{ key: string; label: string; pct: number }>, otherLabel: string): Slice[] {
  const positive = items.filter(item => item.pct > 0);

  const slices: Slice[] = positive.slice(0, ALLOCATION_TOP).map((item, index) => ({
    ...item,
    color: chartPalette[index % chartPalette.length]
  }));

  if (positive.length > ALLOCATION_TOP) {
    const otherPct = positive.slice(ALLOCATION_TOP).reduce((sum, item) => sum + item.pct, 0);

    slices.push({ key: 'other', label: otherLabel, pct: otherPct, color: OTHER_COLOR });
  }

  return slices;
}

/**
 * `SegmentedBar` — a thin segmented bar + legend (reliable, no chart lib), used
 * for both the asset allocation and the per-wallet value distribution.
 */

function SegmentedBar({ slices }: { slices: Slice[] }): JSX.Element {
  return (
    <div>
      <div className={'flex h-3 w-full overflow-hidden rounded-full bg-muted'}>
        {slices.map(slice => (
          <div
            key={slice.key}
            style={{ width: `${slice.pct}%`, backgroundColor: slice.color }}
            title={`${slice.label} · ${slice.pct.toFixed(1)}%`}
          />
        ))}
      </div>

      <div className={'mt-4 flex flex-wrap gap-x-5 gap-y-2'}>
        {slices.map(slice => (
          <span className={'flex items-center gap-1.5 text-xs'} key={slice.key}>
            <span className={'inline-block h-2.5 w-2.5 rounded-full'} style={{ backgroundColor: slice.color }} />

            <span className={'font-medium text-foreground'}>{slice.label}</span>

            <span className={'tabular-nums text-muted-foreground'}>
              {slice.pct.toFixed(1)}
              {'%'}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Export `CryptoGestOverview` component.
 */

export function CryptoGestOverview(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { format: usd } = useDisplayMoney();
  const { data, isLoading } = useCryptoGestOverview();
  const { data: settings } = useCryptoGestSettings();
  const saveInterval = useSaveCryptoGestChartInterval();
  const syncAll = useSyncAllCryptoGestWallets();
  const { success, error } = useToast();

  const onSyncAll = async () => {
    try {
      await syncAll.mutateAsync();
      success({ message: translate('cryptoGest.overview.syncedAll') });
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };
  // Persisted per user; a local override applies immediately while it saves.
  const [intervalOverride, setIntervalOverride] = useState<string | null>(null);
  const [holdingsWallet, setHoldingsWallet] = useQueryState('wallet', 'general');
  const [holdingsAsset, setHoldingsAsset] = useQueryState('asset', 'all');
  const [holdingsSearch, setHoldingsSearch] = useQueryState('q', '');
  const valueInterval = intervalOverride ?? settings?.valueInterval ?? '12h';
  const setValueInterval = (key: string) => {
    setIntervalOverride(key);
    saveInterval.mutate({ valueInterval: key });
  };
  const { data: valueHistory, isFetching: valueFetching } = useCryptoGestValueHistory(valueInterval);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Value-over-time series (reconstructed from klines, back to the first entry).
  // Memoized so the chart keeps a stable data reference (Recharts restarts its
  // animation on every new array).
  const series = useMemo(() => {
    const meta = VALUE_INTERVALS.find(item => item.key === valueInterval) ?? VALUE_INTERVALS[4];

    return (valueHistory ?? []).map(point => ({
      date: moment(point.timestamp).format(meta.intraday ? 'DD MMM HH:mm' : 'DD MMM'),
      fullLabel: moment(point.timestamp).format(meta.intraday ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY'),
      value: point.value
    }));
  }, [valueHistory, valueInterval]);

  const valueDomain = useMemo(() => paddedDomain(series.map(point => point.value)), [series]);

  // Holdings columns (sortable). USD formatter changes each render, so the deps
  // include it; rebuilding the column defs is cheap and the sort state is held
  // outside the table.
  const holdingsColumns = useMemo<ColumnDef<CgAssetRow, any>[]>(
    () => [
      {
        accessorKey: 'symbol',
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        id: 'amount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => amount(row.original.amount),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        accessorKey: 'avgCost',
        header: translate('cryptoGest.labels.avgCost'),
        cell: ({ getValue }) => usd(getValue() as number | null),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'price',
        header: translate('cryptoGest.transactions.currentPrice'),
        cell: ({ getValue }) => usd(getValue() as number),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        accessorKey: 'valueUsd',
        header: translate('cryptoGest.labels.value'),
        cell: ({ getValue }) => usd(getValue() as number),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      },
      {
        accessorKey: 'allocationPct',
        header: translate('cryptoGest.labels.allocation'),
        cell: ({ getValue }) => `${(getValue() as number).toFixed(1)}%`,
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'unrealizedPnlUsd',
        header: translate('cryptoGest.transactions.profitValue'),
        cell: ({ getValue }) => (
          <span className={signClass(getValue() as number | null)}>{usd(getValue() as number | null)}</span>
        ),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      },
      {
        accessorKey: 'profitRate',
        header: translate('cryptoGest.labels.profitRate'),
        cell: ({ getValue }) => (
          <span className={signClass(getValue() as number | null)}>{pct(getValue() as number | null)}</span>
        ),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      }
    ],
    [translate, usd]
  );

  // Keep the chart scrolled to the most recent point; swipe left to go back.
  useEffect(() => {
    const element = scrollRef.current;

    if (element) {
      element.scrollLeft = element.scrollWidth;
    }
  }, [series]);

  if (isLoading) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  const assets = data?.assets ?? [];

  if (!data || assets.length === 0) {
    return (
      <div className={'w-full'}>
        <Title>{translate('cryptoGest.title')}</Title>

        <div
          className={
            'mt-6 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center'
          }
        >
          <div className={'text-base font-semibold text-foreground'}>
            {translate('cryptoGest.overview.empty.title')}
          </div>

          <p className={'max-w-md text-sm text-muted-foreground'}>
            {translate('cryptoGest.overview.empty.description')}
          </p>

          <Button className={'mt-2'} onClick={() => navigate(appRoutes.dashboard.cryptoGest.wallets)}>
            {translate('cryptoGest.wallet.add')}
          </Button>
        </div>
      </div>
    );
  }

  const withRate = assets.filter(asset => asset.profitRate !== null);
  const gainers = [...withRate].sort((a, b) => (b.profitRate ?? 0) - (a.profitRate ?? 0));
  const losers = [...withRate].sort((a, b) => (a.profitRate ?? 0) - (b.profitRate ?? 0));

  const otherLabel = translate('cryptoGest.overview.other');
  const allocationSlices = buildSlices(
    assets.map(asset => ({ key: String(asset.assetId), label: asset.symbol, pct: asset.allocationPct })),
    otherLabel
  );
  const walletSlices = buildSlices(
    (data.walletBreakdown ?? []).map(wallet => ({
      key: wallet.walletId,
      label: wallet.name,
      pct: wallet.allocationPct
    })),
    otherLabel
  );

  return (
    <div className={'w-full space-y-6'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <Title>{translate('cryptoGest.title')}</Title>

        <Button disabled={syncAll.isLoading} onClick={onSyncAll} variant={'outline'}>
          <RefreshCw className={`h-4 w-4 ${syncAll.isLoading ? 'animate-spin' : ''}`} />

          {translate(syncAll.isLoading ? 'cryptoGest.overview.syncingAll' : 'cryptoGest.overview.syncAll')}
        </Button>
      </div>

      {data.needsDataCount > 0 && (
        <button
          className={
            'flex w-full items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-700 transition-colors hover:bg-amber-500/15'
          }
          onClick={() => navigate(appRoutes.dashboard.cryptoGest.transactions)}
          type={'button'}
        >
          <AlertTriangle className={'h-4 w-4 shrink-0'} />

          <span className={'flex-1'}>{translate('cryptoGest.overview.needsData', { count: data.needsDataCount })}</span>

          <span className={'font-medium underline'}>{translate('cryptoGest.overview.review')}</span>
        </button>
      )}

      {/* Totals */}
      <div className={'grid grid-cols-2 gap-4 lg:grid-cols-4'}>
        <StatCard label={translate('cryptoGest.overview.totalValue')} value={usd(data.totalValueUsd)} />

        <StatCard label={translate('cryptoGest.overview.invested')} value={usd(data.totalInvestedUsd)} />

        <StatCard
          label={translate('cryptoGest.overview.pnl')}
          sub={pct(data.totalProfitRate)}
          subClass={signClass(data.totalPnlUsd)}
          value={usd(data.totalPnlUsd)}
        />

        <StatCard
          label={translate('cryptoGest.overview.stablecoins')}
          sub={`${data.stablePct.toFixed(1)}% ${translate('cryptoGest.overview.ofPortfolio')} · ${Math.max(
            0,
            100 - data.stablePct
          ).toFixed(1)}% ${translate('cryptoGest.overview.exposed')}`}
          value={usd(data.stableValueUsd)}
        />
      </div>

      {/* Distribution: asset allocation + value across wallets (thin bars) */}
      <div className={'grid grid-cols-1 gap-4 lg:grid-cols-2'}>
        <div className={'min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm'}>
          <div className={'mb-4 text-sm font-semibold text-foreground'}>
            {translate('cryptoGest.overview.allocation')}
          </div>

          <SegmentedBar slices={allocationSlices} />
        </div>

        <div className={'min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm'}>
          <div className={'mb-4 text-sm font-semibold text-foreground'}>
            {translate('cryptoGest.overview.byWallet')}
          </div>

          {walletSlices.length > 0 ? (
            <SegmentedBar slices={walletSlices} />
          ) : (
            <p className={'text-sm text-muted-foreground'}>{translate('cryptoGest.overview.noHistory')}</p>
          )}
        </div>
      </div>

      {/* Value over time (full width) */}
      <div className={'min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm'}>
        <div className={'mb-3 flex flex-wrap items-center justify-between gap-2'}>
          <div className={'text-sm font-semibold text-foreground'}>
            {translate('cryptoGest.overview.valueOverTime')}
          </div>

          <div className={'flex flex-wrap gap-1'}>
            {VALUE_INTERVALS.map(item => (
              <button
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium tabular-nums transition-colors',
                  valueInterval === item.key
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                key={item.key}
                onClick={() => setValueInterval(item.key)}
                type={'button'}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {valueFetching && series.length <= 1 ? (
          <div className={'grid h-[260px] place-items-center'}>
            <Spinner />
          </div>
        ) : series.length > 1 ? (
          <div className={'overflow-x-auto'} ref={scrollRef}>
            <div style={{ width: Math.max(600, series.length * 16) }}>
              <AreaTrendChart
                data={series}
                series={[
                  { key: 'value', label: translate('cryptoGest.overview.totalValue'), color: chartColors.primary }
                ]}
                valueFormatter={usd}
                xKey={'date'}
                yDomain={valueDomain}
              />
            </div>
          </div>
        ) : (
          <div className={'grid h-[260px] place-items-center text-center text-sm text-muted-foreground'}>
            {translate('cryptoGest.overview.noHistory')}
          </div>
        )}
      </div>

      {/* Top movers */}
      {(data.topGainers.length > 0 || data.topLosers.length > 0) && (
        <div className={'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
          <MoverList
            all={gainers}
            onClick={id => navigate(`/dashboard/crypto-gest/assets/${id}`)}
            title={translate('cryptoGest.overview.topGainers')}
          />

          <MoverList
            all={losers}
            onClick={id => navigate(`/dashboard/crypto-gest/assets/${id}`)}
            title={translate('cryptoGest.overview.topLosers')}
          />
        </div>
      )}

      {/* Holdings — General (merged by asset) or a specific wallet, with asset +
          text filters. */}
      {(() => {
        const baseHoldings =
          holdingsWallet === 'general'
            ? assets
            : data.walletHoldings?.find(wallet => wallet.walletId === holdingsWallet)?.assets ?? [];

        const query = holdingsSearch.trim().toLowerCase();
        const filtered = baseHoldings.filter(
          asset =>
            (holdingsAsset === 'all' || asset.symbol === holdingsAsset) &&
            (!query || asset.symbol.toLowerCase().includes(query) || (asset.name ?? '').toLowerCase().includes(query))
        );

        const walletOptions = [
          { value: 'general', label: translate('cryptoGest.overview.general') },
          ...(data.walletHoldings ?? []).map(wallet => ({ value: wallet.walletId, label: wallet.name }))
        ];
        const assetOptions = [
          { value: 'all', label: translate('cryptoGest.transactions.allAssets') },
          ...Array.from(new Set(baseHoldings.map(asset => asset.symbol)))
            .sort()
            .map(symbol => ({ value: symbol, label: symbol }))
        ];
        const walletQuery = holdingsWallet === 'general' ? '' : `?wallet=${holdingsWallet}`;

        return (
          <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
            <div className={'flex flex-wrap items-center justify-between gap-2 px-4 py-3'}>
              <span className={'text-sm font-semibold text-foreground'}>
                {translate('cryptoGest.overview.holdings')}
              </span>

              <div className={'flex flex-wrap items-center gap-2'}>
                <div className={'relative min-w-[160px]'}>
                  <Search
                    className={
                      'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground'
                    }
                  />

                  <Input
                    className={'h-9 pl-8'}
                    onChange={event => setHoldingsSearch(event.target.value)}
                    placeholder={translate('cryptoGest.transactions.searchPlaceholder')}
                    value={holdingsSearch}
                  />
                </div>

                <div className={'w-36'}>
                  <Combobox
                    onChange={value => setHoldingsAsset(value ?? 'all')}
                    options={assetOptions}
                    value={holdingsAsset}
                  />
                </div>

                <div className={'w-44'}>
                  <Combobox
                    onChange={value => {
                      setHoldingsWallet(value ?? 'general');
                      setHoldingsAsset('all');
                    }}
                    options={walletOptions}
                    value={holdingsWallet}
                  />
                </div>
              </div>
            </div>

            <DataTable
              columns={holdingsColumns}
              data={filtered}
              initialSorting={[{ id: 'profitRate', desc: true }]}
              onRowClick={asset => navigate(`/dashboard/crypto-gest/assets/${asset.assetId}${walletQuery}`)}
            />
          </div>
        );
      })()}
    </div>
  );
}

/**
 * A compact top-movers list.
 */

function MoverRow({ asset, onClick }: { asset: CgAssetRow; onClick: (assetId: number) => void }): JSX.Element {
  return (
    <button
      className={'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-muted/40'}
      onClick={() => onClick(asset.assetId)}
      type={'button'}
    >
      <span className={'flex items-center gap-2 font-medium text-foreground'}>
        <AssetIcon logo={asset.logo} symbol={asset.symbol} />

        {asset.symbol}
      </span>

      <span className={`font-medium tabular-nums ${signClass(asset.profitRate)}`}>{pct(asset.profitRate)}</span>
    </button>
  );
}

function MoverList({
  title,
  all,
  onClick
}: {
  title: string;
  all: CgAssetRow[];
  onClick: (assetId: number) => void;
}): JSX.Element {
  const [translate] = useTranslation();
  const { format: usd } = useDisplayMoney();
  const [open, setOpen] = useState(false);

  const columns = useMemo<ColumnDef<CgAssetRow, any>[]>(
    () => [
      {
        accessorKey: 'symbol',
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        accessorKey: 'avgCost',
        header: translate('cryptoGest.labels.avgCost'),
        cell: ({ getValue }) => usd(getValue() as number | null),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'price',
        header: translate('cryptoGest.transactions.currentPrice'),
        cell: ({ getValue }) => usd(getValue() as number),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        accessorKey: 'costBasisUsd',
        header: translate('cryptoGest.overview.invested'),
        cell: ({ getValue }) => usd(getValue() as number | null),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'unrealizedPnlUsd',
        header: translate('cryptoGest.transactions.profitValue'),
        cell: ({ getValue }) => (
          <span className={signClass(getValue() as number | null)}>{usd(getValue() as number | null)}</span>
        ),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      },
      {
        accessorKey: 'profitRate',
        header: translate('cryptoGest.labels.profitRate'),
        cell: ({ getValue }) => (
          <span className={signClass(getValue() as number | null)}>{pct(getValue() as number | null)}</span>
        ),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      }
    ],
    [translate, usd]
  );

  if (all.length === 0) {
    return <div />;
  }

  return (
    <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>
      <div className={'-mx-4 mb-3 flex items-center justify-between border-b border-border px-4 pb-2.5'}>
        <div className={'text-sm font-semibold text-foreground'}>{title}</div>

        {all.length > 3 && (
          <button
            className={'text-xs font-medium text-primary hover:underline'}
            onClick={() => setOpen(true)}
            type={'button'}
          >
            {translate('cryptoGest.overview.viewMore')}
          </button>
        )}
      </div>

      <div className={'space-y-1'}>
        {all.slice(0, 3).map(asset => (
          <MoverRow asset={asset} key={asset.assetId} onClick={onClick} />
        ))}
      </div>

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent className={'max-w-3xl'}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className={'-mr-2 max-h-[60vh] overflow-y-auto pr-3'}>
            <DataTable
              columns={columns}
              data={all}
              onRowClick={asset => {
                setOpen(false);
                onClick(asset.assetId);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
